import { useEffect, useRef } from 'react';
import { useToastStore } from './toastStore';

const AUTO_DISMISS_MS = 4000;
// A deliberate flick, not an accidental drag-scroll — same order of magnitude as BottomSheet's
// own swipe-dismiss gesture threshold.
const SWIPE_DISMISS_PX = 40;

/**
 * SCREENS.md "Global additions": the app-wide toast — `aria-live="polite"` (closing the
 * documented Phase 5 accessibility gap: "No toast/snackbar infrastructure exists anywhere in
 * this codebase"), bottom-anchored above the bottom nav, one at a time, auto-dismiss + swipe-to-
 * dismiss. Mounted once in App.tsx (not per-route) so it's available across every screen — full-
 * screen routes outside `<AppShell>` (the wardrobe, `/claim-handle`, ...) included, not just the
 * tabbed shell.
 *
 * `role="status"` (not `role="alert"`) + `aria-live="polite"`: same "announced without stealing
 * focus or interrupting" idiom `ShellsHud`'s own balance-change span already uses — a toast is
 * good news or a confirmation far more often than an urgent interruption, and `polite` queues
 * behind whatever the screen reader is already saying rather than cutting it off.
 */
export function Toast() {
  const toast = useToastStore((s) => s.toast);
  const exiting = useToastStore((s) => s.exiting);
  const beginDismiss = useToastStore((s) => s.beginDismiss);
  const clear = useToastStore((s) => s.clear);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (!toast || exiting) return;
    // Remainder, not a fresh 4s: this component remounts on some navigations, and restarting
    // the timer each time kept a craft toast on screen across Bag, Anvil and Hearth.
    const wait = Math.max(0, AUTO_DISMISS_MS - (Date.now() - toast.shownAt));
    const timer = setTimeout(beginDismiss, wait);
    return () => clearTimeout(timer);
  }, [toast, exiting, beginDismiss]);

  useEffect(() => {
    if (!exiting) return;
    // animationend is the normal clear. A view transition can cancel toast-out before it
    // finishes, and then nothing else removes the toast.
    const backup = setTimeout(clear, 800);
    return () => clearTimeout(backup);
  }, [exiting, clear]);

  if (!toast) return null;

  return (
    <div className="toast-region">
      <div
        className={`toast ${exiting ? 'toast-out' : 'toast-in'}`}
        role="status"
        aria-live="polite"
        onAnimationEnd={() => { if (exiting) clear(); }}
        onTouchStart={(e) => { touchStartX.current = e.touches[0]?.clientX ?? null; }}
        onTouchEnd={(e) => {
          const startX = touchStartX.current;
          touchStartX.current = null;
          if (startX == null) return;
          const endX = e.changedTouches[0]?.clientX;
          if (endX == null) return;
          // A finger that has already flicked the toast off screen shouldn't then watch the
          // leave animation (--t-leave) play out — skip straight to clear(), not beginDismiss(). (raw-ok: prose)
          if (Math.abs(endX - startX) > SWIPE_DISMISS_PX) clear();
        }}
      >
        <span>{toast.text}</span>
        <div className="toast-actions">
          {toast.action && (
            <button
              type="button"
              className="btn-quiet"
              onClick={() => { toast.action!.onClick(); beginDismiss(); }}
            >
              {toast.action.label}
            </button>
          )}
          <button type="button" className="toast-dismiss" onClick={beginDismiss}>Dismiss</button>
        </div>
      </div>
    </div>
  );
}
